/* eslint-disable @repo/internal/react/no-class-components */
import React, { type ErrorInfo } from 'react';

import { type CreateUIAnalyticsEvent, withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';

import { type RemotePlugin } from '../../../types';

interface ErrorBoundaryProps {
	analyticsChannel?: string;
	children: React.ReactNode;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	fallback?: React.ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	remotePlugin: RemotePlugin;
}

interface ErrorBoundaryState {
	hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const analyticsEvent = this.props.createAnalyticsEvent({
			action: 'handled',
			actionSubject: 'error',
			actionSubjectId: 'remoteModuleErrorBoundary',
			attributes: {
				error: error?.message,
				errorInfo,
				dimensions: this.props.remotePlugin.dimensions,
				plugin: this.props.remotePlugin.constructor.name,
			},
		});
		analyticsEvent.fire(this.props.analyticsChannel);

		if (this.props.onError) {
			this.props.onError(error, errorInfo);
		}

		this.setState({
			hasError: true,
		});
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback ? <>{this.props.fallback}</> : null;
		}

		return this.props.children;
	}
}

const _default_1: React.ForwardRefExoticComponent<Omit<ErrorBoundaryProps, keyof WithAnalyticsEventsProps> & React.RefAttributes<any>> = withAnalyticsEvents()(ErrorBoundary);
export default _default_1;
